import {useEffect, useRef, useState} from 'react';
import {Layers, Send} from 'lucide-react';
import {
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Kbd,
  NativeSelect,
  NativeSelectOption,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {type ModerationTask} from '@/api/moderationQueue';

export const MAX_REASON_LENGTH = 1000;

type Props = {
  readonly task: ModerationTask;
  readonly busy: boolean;
  // Another dialog is open over the page: the shortcuts must not reach through it.
  readonly shortcutsPaused: boolean;
  // Opens with the reclassify form showing.
  readonly initialReclassifying?: boolean;
  // Where the item can be reclassified to (its own policy violation first), and where this analyst
  // may classify it themselves.
  readonly targets: readonly string[];
  readonly directTargets: ReadonlySet<string>;
  readonly inline?: boolean;
  readonly onClassify: () => void;
  readonly onDismiss: () => void;
  readonly onSkip: () => void;
  readonly onSend: (target: string, reason: string) => void;
  readonly onClassifyDirectly: (target: string, reason: string) => void;
};

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

// What the analyst decides about the held task. V opens the classify dialog, offering only the
// engine's suggestion, N is not violative, S skips and R opens the reclassify form for any other
// classification; the shortcuts are ignored while typing. Mount it with key={task.id} so the form
// starts empty for each task.
export function QueueDecisionPanel({
  task,
  busy,
  shortcutsPaused,
  initialReclassifying = false,
  targets,
  directTargets,
  inline = false,
  onClassify,
  onDismiss,
  onSkip,
  onSend,
  onClassifyDirectly
}: Props) {
  const [reclassifying, setReclassifying] = useState(initialReclassifying);
  const [target, setTarget] = useState(targets[0] ?? '');
  const [reason, setReason] = useState('');

  // Read through a ref so the one window listener always sees the current props.
  const canReclassify = targets.length > 0;
  const latest = useRef({
    busy,
    shortcutsPaused,
    reclassifying,
    canReclassify,
    onClassify,
    onDismiss,
    onSkip
  });
  useEffect(() => {
    latest.current = {
      busy,
      shortcutsPaused,
      reclassifying,
      canReclassify,
      onClassify,
      onDismiss,
      onSkip
    };
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const {current} = latest;
      if (
        event.repeat ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isTyping(event.target) ||
        current.shortcutsPaused ||
        current.busy ||
        current.reclassifying
      ) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'v': {
          current.onClassify();
          break;
        }

        case 'n': {
          current.onDismiss();
          break;
        }

        case 's': {
          current.onSkip();
          break;
        }

        case 'r': {
          if (!current.canReclassify) {
            return;
          }

          setReclassifying(true);
          break;
        }

        default: {
          return;
        }
      }

      event.preventDefault();
    };

    globalThis.addEventListener('keydown', onKeyDown);
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const inOwnQueue = target === task.policy_violation;
  const canSend = !busy && target !== '' && !inOwnQueue && !task.is_reclassified;
  const canClassifyDirectly = !busy && directTargets.has(target);

  return (
    <aside
      className={
        inline
          ? 'border-border bg-background flex max-h-[55%] shrink-0 flex-col border-t'
          : 'border-border bg-background flex min-h-0 w-85 shrink-0 flex-col border-l'
      }>
      {reclassifying ? (
        <div className="flex flex-1 flex-col gap-4 overflow-auto p-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Reclassify</span>
            <span className="text-muted-foreground text-xs/relaxed">
              Closes this task as misclassified by the engine. Classify it yourself under another
              classification in {task.policy_violation} or another violation, or send it to another
              vertical&apos;s queue for a specialist.
            </span>
          </div>
          <Field>
            <FieldLabel htmlFor="queue-reclassify-target">Move to violation</FieldLabel>
            <NativeSelect
              id="queue-reclassify-target"
              value={target}
              aria-label="Target policy violation"
              onChange={(event) => {
                setTarget(event.target.value);
              }}>
              {targets.map((name) => (
                <NativeSelectOption key={name} value={name}>
                  {name === task.policy_violation ? `${name} (this queue)` : name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor="queue-reclassify-reason">Why is the engine wrong?</FieldLabel>
            <Textarea
              id="queue-reclassify-reason"
              rows={3}
              maxLength={MAX_REASON_LENGTH}
              value={reason}
              placeholder="What does this content actually show?"
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <FieldDescription>
              Optional. If you send it on, the analyst who reviews it next sees it.
            </FieldDescription>
          </Field>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="border-border bg-card flex flex-col gap-2 border-t p-4">
        {reclassifying ? (
          <>
            {inOwnQueue ? null : (
              <Button
                type="button"
                disabled={!canSend}
                title={
                  task.is_reclassified
                    ? 'Another analyst already sent this item here, so it cannot be sent on again.'
                    : undefined
                }
                onClick={() => {
                  onSend(target, reason.trim());
                }}>
                Send to {target} queue
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              disabled={!canClassifyDirectly}
              title={
                directTargets.has(target)
                  ? undefined
                  : 'You are not permitted to classify in this policy violation.'
              }
              onClick={() => {
                onClassifyDirectly(target, reason.trim());
              }}>
              Classify directly in {target}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setReclassifying(false);
              }}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button type="button" disabled={busy} onClick={onClassify}>
              <Send />
              Classify
              <Kbd className="ml-auto">V</Kbd>
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                title="Recorded decision: not violative for this vertical"
                onClick={onDismiss}>
                Not violative
                <Kbd className="ml-auto">N</Kbd>
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                title="Return to the pool for someone else"
                onClick={onSkip}>
                Skip
                <Kbd className="ml-auto">S</Kbd>
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy || !canReclassify}
              onClick={() => {
                setReclassifying(true);
              }}>
              <Layers />
              Wrong classification? Reclassify
              <Kbd className="ml-auto">R</Kbd>
            </Button>
          </>
        )}
      </div>
    </aside>
  );
}
